import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../../lib/api.js";
import { Link, useRouter } from "../../lib/router.js";
import { PageSpinner, EmptyState, Modal } from "../../components/ui.js";
import { ExercisePicker } from "../../components/ExercisePicker.js";
import { QuickLogModal } from "../../components/QuickLogModal.js";
import { IconDumbbell, IconHeart } from "../../components/icons.js";

interface Status {
  inRecovery: boolean;
  canTrainResistance: boolean;
  restrictions: { key: string; note: string | null }[];
  recentWorkouts: { id: string; name: string; date: string; status: string; isHomeBackup: boolean; isMinimum: boolean; exerciseCount: number; setCount: number }[];
  recentWalks: { id: string; sessionDate: string; durationMinutes: number | null }[];
  prs: { name: string; weightLb: number; reps: number; e1rm: number }[];
}
interface Program { id: string; name: string; days: { id: string; name: string; exercises: { name: string }[] }[] }

function localDate(): string { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; }

export function TrainingPage() {
  const { navigate } = useRouter();
  const [status, setStatus] = useState<Status | null>(null);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [pickingQuick, setPickingQuick] = useState(false);
  const [buildingProgram, setBuildingProgram] = useState(false);
  const [planBDay, setPlanBDay] = useState<{ id: string; name: string } | null>(null);
  const [walkModal, setWalkModal] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, p] = await Promise.all([api.get<Status>("/api/training/status"), api.get<{ programs: Program[] }>("/api/programs")]);
      setStatus(s);
      setPrograms(p.programs);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load training.");
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  async function startQuick(picked: { exerciseRef: string; name: string }[]) {
    setPickingQuick(false);
    try {
      const res = await api.post<{ workout: { id: string } }>("/api/workouts", { name: "Quick workout", date: localDate(), exercises: picked.map((p) => ({ exerciseRef: p.exerciseRef })) });
      navigate(`/workout/${res.workout.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not start workout.");
    }
  }

  if (!status) return <div><div className="page-header"><h1>Training</h1></div>{error ? <div className="alert alert-danger">{error}</div> : <PageSpinner />}</div>;

  return (
    <div>
      <div className="page-header">
        <h1>Training</h1>
        <p className="subtitle">{status.canTrainResistance ? "Build strength and the body you're after." : "Movement that fits where you are right now."}</p>
      </div>
      {error && <div className="alert alert-danger">{error}</div>}

      {!status.canTrainResistance ? (
        <div className="stack">
          <div className="alert alert-info">
            <strong>Training is on hold while you heal.</strong> Resistance and strenuous work are restricted. Walking is your movement — build it gently, as tolerated.
            {status.inRecovery && <> When your clinician clears you, record it in <Link to="/recovery">Recovery</Link> and training unlocks.</>}
          </div>
          <div className="row wrap">
            <button className="btn btn-primary" onClick={() => setWalkModal(true)}><IconHeart width={16} height={16} /> Log a walk</button>
            <Link to="/recovery" className="btn">Recovery</Link>
          </div>
          {status.recentWalks.length > 0 && (
            <div className="card">
              <div className="card-header"><span className="card-title">Recent walks</span></div>
              <div className="table-card" style={{ border: "none", padding: 0 }}>
                {status.recentWalks.map((w) => (
                  <div className="tc-row" key={w.id}><span className="tc-label">{w.sessionDate}</span><span>{w.durationMinutes ? `${w.durationMinutes} min` : "logged"}</span></div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="stack">
          <div className="row wrap">
            <button className="btn btn-primary" onClick={() => setPickingQuick(true)}><IconDumbbell width={16} height={16} /> Start a workout</button>
            <button className="btn" onClick={() => setBuildingProgram(true)}>New program</button>
          </div>

          {/* Programs */}
          {programs.map((prog) => (
            <div className="card" key={prog.id}>
              <div className="card-header"><span className="card-title">{prog.name}</span></div>
              <div className="stack">
                {prog.days.map((day) => (
                  <div className="row-between" key={day.id}>
                    <div>
                      <div style={{ fontWeight: 600 }}>{day.name}</div>
                      <div className="dim text-xs">{day.exercises.map((e) => e.name).join(", ")}</div>
                    </div>
                    <button className="btn btn-sm" onClick={() => setPlanBDay({ id: day.id, name: day.name })}>Start</button>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* PRs */}
          {status.prs.length > 0 && (
            <div className="card">
              <div className="card-header"><span className="card-title">Personal records</span></div>
              <div className="table-card" style={{ border: "none", padding: 0 }}>
                {status.prs.map((pr) => (
                  <div className="tc-row" key={pr.name}><span className="tc-label">{pr.name}</span><span><strong>{pr.weightLb} × {pr.reps}</strong> <span className="dim">(~{pr.e1rm} 1RM)</span></span></div>
                ))}
              </div>
            </div>
          )}

          {/* Recent workouts */}
          {status.recentWorkouts.length > 0 ? (
            <div className="card">
              <div className="card-header"><span className="card-title">Recent workouts</span></div>
              <div className="stack">
                {status.recentWorkouts.map((w) => (
                  <Link to={`/workout/${w.id}`} className="row-between" key={w.id} style={{ color: "inherit" }}>
                    <div>
                      <div style={{ fontWeight: 600 }}>{w.name} {w.isHomeBackup && <span className="badge badge-muted">home</span>} {w.isMinimum && <span className="badge badge-muted">min</span>}</div>
                      <div className="dim text-xs">{w.date} · {w.exerciseCount} exercises · {w.setCount} sets</div>
                    </div>
                    <span className={`badge ${w.status === "completed" ? "badge-success" : "badge-accent"}`}>{w.status === "completed" ? "done" : "open"}</span>
                  </Link>
                ))}
              </div>
            </div>
          ) : (
            programs.length === 0 && <EmptyState icon={<IconDumbbell />} title="No workouts yet">Start a quick workout, or build a program you can run each week.</EmptyState>
          )}
        </div>
      )}

      {pickingQuick && <ExercisePicker title="Build your workout" onClose={() => setPickingQuick(false)} onPick={startQuick} />}
      {buildingProgram && <ProgramBuilder onClose={() => setBuildingProgram(false)} onSaved={async () => { setBuildingProgram(false); await load(); }} />}
      {planBDay && <PlanBModal day={planBDay} onClose={() => setPlanBDay(null)} onStart={(id) => navigate(`/workout/${id}`)} />}
      {walkModal && <QuickLogModal type="walk" onClose={() => setWalkModal(false)} onLogged={load} />}
    </div>
  );
}

const PLAN_B_REASONS = ["Snowstorm", "Gym unavailable", "Traveling", "Feeling sick", "Poor sleep", "Too busy", "Low motivation", "Sore", "Other"];

function PlanBModal({ day, onClose, onStart }: { day: { id: string; name: string }; onClose: () => void; onStart: (workoutId: string) => void }) {
  const [reason, setReason] = useState("Gym unavailable");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function start(mode: "normal" | "home" | "minimum") {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ workout: { id: string } }>("/api/workouts", {
        programDayId: day.id, date: localDate(),
        isHomeBackup: mode === "home", isMinimum: mode === "minimum",
        planBReason: mode === "normal" ? null : reason
      });
      onStart(res.workout.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not start.");
      setBusy(false);
    }
  }

  return (
    <Modal title={`Start ${day.name}`} onClose={onClose}>
      {error && <div className="alert alert-danger">{error}</div>}
      <button className="btn btn-primary btn-block" onClick={() => start("normal")} disabled={busy}>Start normally</button>
      <div className="mt-6">
        <div className="nav-section-label" style={{ padding: 0 }}>Life happened?</div>
        <div className="field mt-2">
          <label>Reason</label>
          <select className="select" value={reason} onChange={(e) => setReason(e.target.value)}>
            {PLAN_B_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div className="row wrap">
          <button className="btn grow" onClick={() => start("home")} disabled={busy}>Home backup</button>
          <button className="btn grow" onClick={() => start("minimum")} disabled={busy}>Minimum version</button>
        </div>
        <p className="dim text-xs mt-4" style={{ marginBottom: 0 }}>Home backup swaps gym lifts for home alternatives. A minimum version keeps the thread going — it still counts.</p>
      </div>
    </Modal>
  );
}

function ProgramBuilder({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState("");
  const [days, setDays] = useState<{ name: string; exercises: { exerciseRef: string; name: string }[] }[]>([{ name: "Day 1", exercises: [] }]);
  const [pickingFor, setPickingFor] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setError(null);
    const valid = days.filter((d) => d.name.trim() && d.exercises.length > 0);
    if (!name.trim() || valid.length === 0) { setError("Name the program and add exercises to at least one day."); return; }
    setSaving(true);
    try {
      await api.post("/api/programs", { name, days: valid.map((d) => ({ name: d.name, exercises: d.exercises.map((e) => ({ exerciseRef: e.exerciseRef, name: e.name, targetSets: 3, targetReps: "8-12" })) })) });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save.");
      setSaving(false);
    }
  }

  return (
    <Modal title="New program" onClose={onClose}>
      {error && <div className="alert alert-danger">{error}</div>}
      <div className="field"><label>Program name</label><input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Upper / Lower" /></div>
      {days.map((day, i) => (
        <div className="card" key={i} style={{ background: "var(--surface-1)", marginBottom: "var(--space-3)" }}>
          <input className="input" value={day.name} onChange={(e) => setDays((prev) => prev.map((d, idx) => idx === i ? { ...d, name: e.target.value } : d))} placeholder="Day name" />
          {day.exercises.length > 0 && <div className="dim text-xs mt-2">{day.exercises.map((x) => x.name).join(", ")}</div>}
          <button className="btn btn-sm mt-2" onClick={() => setPickingFor(i)}>+ Add exercises</button>
        </div>
      ))}
      <button className="btn btn-sm" onClick={() => setDays([...days, { name: `Day ${days.length + 1}`, exercises: [] }])}>+ Add day</button>
      <button className="btn btn-primary btn-block mt-4" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save program"}</button>
      {pickingFor !== null && (
        <ExercisePicker onClose={() => setPickingFor(null)} onPick={(picked) => {
          setDays((prev) => prev.map((d, idx) => idx === pickingFor ? { ...d, exercises: [...d.exercises, ...picked] } : d));
          setPickingFor(null);
        }} />
      )}
    </Modal>
  );
}
